import { Modal } from '@appkit4/react-components/modal';
import useGenericErrorToast from '../helpers/useGenericErrorToast';
import CreateUserForm from '../components/AdminCreateUserForm';
import { FormValues } from './AdminCreateUserForm';
import useToast from '../helpers/useToast';
import { BusinessUnitResponse } from '../api/generated/models';
import useLocale from '../locale/useLocale';
import ModalContainer from './ModalContainer';
import {
    PutV1UserAdminUserIdMutationError,
    getGetV1BusinessUnitSuperAdminQueryKey,
    getGetV1UserAdminQueryKey,
    useGetV1IdentityUserInfo,
    useGetV1RoleAdminAll,
    usePostV1UserAdmin,
} from '../api/generated/endpoint';
import { useQueryClient } from '@tanstack/react-query';

interface IAdminCreateUserProps {
    isVisible: boolean;
    setIsVisible: (display: boolean) => void;
    businessUnits: BusinessUnitResponse[] | null;
    currentBusinessUnitId: number | undefined;
}

const AdminCreateUserModal = (props: IAdminCreateUserProps) => {
    const showToast = useToast();
    const { l } = useLocale();
    const showGenericErrorToast = useGenericErrorToast();
    const createNewUser = usePostV1UserAdmin();
    const queryClient = useQueryClient();

    const { data: roles } = useGetV1RoleAdminAll({
        query: {
            onSuccess: (data) => {
                if (!data || data.length < 1) {
                    showToast(
                        l('_error'),
                        l('_noRolesFoundUnableToCreateAUserRightNow'),
                        'error'
                    );
                    props.setIsVisible(false);
                }
            },
            onError: (error) => {
                showGenericErrorToast(error);
                props.setIsVisible(false);
            },
            staleTime: Infinity,
        },
    });

    const { data: userInfo } = useGetV1IdentityUserInfo({
        query: {
            staleTime: Infinity,
        },
    });

    const postNewUserRequest = (values: FormValues) => {
        createNewUser.mutate(
            {
                data: {
                    email: values.email,
                    surname: values.surname,
                    forenames: values.forenames,
                    roleId: values.roleId,
                    businessUnitId: values.businessUnitId,
                },
            },
            {
                onSuccess: () => {
                    showToast(l('_success'), l('_newUserCreated'), 'success');
                    props.setIsVisible(false);
                    queryClient.invalidateQueries(getGetV1UserAdminQueryKey());
                    queryClient.invalidateQueries(
                        getGetV1BusinessUnitSuperAdminQueryKey()
                    );
                },
                onError: (error: PutV1UserAdminUserIdMutationError) => {
                    showGenericErrorToast(error);
                    props.setIsVisible(false);
                },
            }
        );
    };

    return (
        <>
            {props.isVisible && (
                <ModalContainer isVisible={props.isVisible}>
                    <Modal
                        visible
                        closable
                        closeAriaLabel={l('_close')}
                        title={l('_createNewUser')}
                        aria-label={l('_createANewUser')}
                        modalStyle={{
                            width: '50vw',
                            maxWidth: '500px',
                            minWidth: '250px',
                        }}
                        closeOnPressEscape
                        onCancel={() => props.setIsVisible(false)}
                    >
                        <CreateUserForm
                            roles={
                                // filter roles to only allow roles up to and including current user's role
                                roles?.filter(
                                    (r) => r.id <= (userInfo?.roleId ?? -1)
                                ) ?? []
                            }
                            submitFunc={postNewUserRequest}
                            businessUnits={props.businessUnits}
                            currentBusinessUnitId={props.currentBusinessUnitId}
                        />
                    </Modal>
                </ModalContainer>
            )}
        </>
    );
};

export default AdminCreateUserModal;
